import * as React from 'react';
import { slot, useMergedRefs } from '@fluentui/react-utilities';
import { useVirtualizer_unstable } from '../Virtualizer/useVirtualizer';
import type {
  VirtualizerScrollViewDynamicProps,
  VirtualizerScrollViewDynamicState,
} from './VirtualizerScrollViewDynamic.types';
import { useDynamicVirtualizerMeasure } from '../../Hooks';
import {
  useVirtualizerContextState_unstable,
  scrollToItemDynamic,
} from '../../Utilities';
import type { VirtualizerDataRef } from '../Virtualizer/Virtualizer.types';
import { useMeasureList } from '../../hooks/useMeasureList';
import type { IndexedResizeCallbackElement } from '../../hooks/useMeasureList';
import { useDynamicVirtualizerPagination } from '../../hooks/useDynamicPagination';

export function useVirtualizerScrollViewDynamic_unstable(
  props: VirtualizerScrollViewDynamicProps
): VirtualizerScrollViewDynamicState {
  'use no memo';

  const contextState = useVirtualizerContextState_unstable(
    props.virtualizerContext
  );
  const {
    imperativeRef,
    axis = 'vertical',
    reversed,
    imperativeVirtualizerRef,
    enablePagination = false,
    bufferItems: _bufferItems,
    bufferSize: _bufferSize,
    enableScrollAnchor,
    gap = 0,
  } = props;

  const sizeTrackingArray = React.useRef<number[]>(
    new Array(props.numItems).fill(props.itemSize)
  );

  const getChildSizeAuto = (index: number) => {
    if (
      sizeTrackingArray.current.length <= index ||
      sizeTrackingArray.current[index] <= 0
    ) {
      // Default size for initial state or untracked
      return props.itemSize;
    }
    /* Required to be defined prior to our measure function
     * we use a sizing array ref that we will update post-render
     */
    return sizeTrackingArray.current[index];
  };

  const {
    virtualizerLength,
    bufferItems,
    bufferSize,
    scrollRef,
    containerSizeRef,
    updateScrollPosition,
  } = useDynamicVirtualizerMeasure({
    defaultItemSize: props.itemSize,
    direction: props.axis ?? 'vertical',
    getItemSize: props.getItemSize ?? getChildSizeAuto,
    virtualizerContext: contextState,
    numItems: props.numItems,
    bufferItems: _bufferItems,
    bufferSize: _bufferSize,
    gap,
  });

  const _imperativeVirtualizerRef = useMergedRefs(
    React.useRef<VirtualizerDataRef>(null),
    imperativeVirtualizerRef
  );

  const paginationRef = useDynamicVirtualizerPagination(
    {
      axis,
      progressiveItemSizes: _imperativeVirtualizerRef.current?.progressiveSizes,
      actualNodeSizes: sizeTrackingArray,
      virtualizerLength,
      currentIndex: contextState?.contextIndex ?? 0,
    },
    enablePagination
  );

  // Store the virtualizer length as a ref for imperative ref access
  const virtualizerLengthRef = React.useRef<number>(virtualizerLength);
  if (virtualizerLengthRef.current !== virtualizerLength) {
    virtualizerLengthRef.current = virtualizerLength;
  }
  const localScrollRef = React.useRef<HTMLDivElement>(null);
  const scrollViewRef = useMergedRefs(
    props.scrollViewRef,
    scrollRef,
    paginationRef,
    localScrollRef
  );
  const scrollCallbackRef = React.useRef<null | ((index: number) => void)>(
    null
  );

  React.useImperativeHandle(
    imperativeRef,
    () => {
      return {
        scrollToPosition(
          position: number,
          behavior: ScrollBehavior = 'auto',
          index?: number, // So we can callback when index rendered
          callback?: (index: number) => void
        ) {
          if (callback) {
            scrollCallbackRef.current = callback ?? null;
          }

          if (_imperativeVirtualizerRef.current) {
            if (index !== undefined) {
              _imperativeVirtualizerRef.current.setFlaggedIndex(index);
            }
            const positionOptions =
              axis == 'vertical' ? { top: position } : { left: position };
            scrollViewRef.current?.scrollTo({
              behavior,
              ...positionOptions,
            });
          }
        },
        scrollTo(
          index: number,
          behavior = 'auto',
          callback: undefined | ((index: number) => void)
        ) {
          scrollCallbackRef.current = callback ?? null;
          if (_imperativeVirtualizerRef.current) {
            const progressiveSizes =
              _imperativeVirtualizerRef.current.progressiveSizes.current;
            const totalSize =
              progressiveSizes && progressiveSizes?.length > 0
                ? progressiveSizes[Math.max(progressiveSizes.length - 1, 0)]
                : 0;

            _imperativeVirtualizerRef.current.setFlaggedIndex(index);
            scrollToItemDynamic({
              index,
              getItemSize: props.getItemSize ?? getChildSizeAuto,
              totalSize,
              scrollViewRef: scrollViewRef as React.RefObject<HTMLDivElement>,
              axis,
              reversed,
              behavior,
              gap,
            });
          }
        },
        currentIndex: _imperativeVirtualizerRef.current?.currentIndex,
        virtualizerLength: virtualizerLengthRef,
        sizeTrackingArray,
      };
    },
    [axis, scrollViewRef, reversed, _imperativeVirtualizerRef]
  );

  const handleRenderedIndex = (index: number) => {
    if (scrollCallbackRef.current) {
      scrollCallbackRef.current(index);
    }
  };

  const virtualizerState = useVirtualizer_unstable({
    ...props,
    getItemSize: props.getItemSize ?? getChildSizeAuto,
    virtualizerLength,
    bufferItems,
    bufferSize,
    virtualizerContext: contextState,
    imperativeVirtualizerRef: _imperativeVirtualizerRef,
    onRenderedFlaggedIndex: handleRenderedIndex,
    containerSizeRef,
    scrollViewRef,
    updateScrollPosition,
  });

  const requestScrollBy = React.useCallback(
    (sizeChange: number) => {
      // Handle any size changes so that scroll view doesn't jump around
      if (enableScrollAnchor) {
        localScrollRef.current?.scrollBy({
          top: axis === 'vertical' ? sizeChange : 0,
          left: axis === 'vertical' ? 0 : sizeChange,
          behavior: 'instant',
        });
      }
    },
    [enableScrollAnchor, axis, localScrollRef]
  );

  const measureObject = useMeasureList({
    currentIndex: Math.max(virtualizerState.virtualizerStartIndex, 0),
    totalLength: props.numItems,
    defaultItemSize: props.itemSize,
    sizeTrackingArray,
    axis,
    virtualizerLength,
    requestScrollBy,
  });

  // Enables auto-measuring and tracking post render sizes externally
  React.Children.map(virtualizerState.virtualizedChildren, (child, index) => {
    if (React.isValidElement(child)) {
      virtualizerState.virtualizedChildren[index] = (
        <child.type
          {...child.props}
          key={child.key}
          ref={(element: HTMLElement & IndexedResizeCallbackElement) => {
            if (Object.prototype.hasOwnProperty.call(child, 'ref')) {
              // We must access this from the child directly, not props (forward ref).
              // eslint-disable-next-line  @typescript-eslint/no-explicit-any
              const localRef = (child as any)?.ref;

              if (typeof localRef === 'function') {
                localRef(element);
              } else if (localRef) {
                localRef.current = element;
              }
            }

            // Call the auto-measure ref attachment.
            measureObject.createIndexedRef(index)(element);
          }}
        />
      );
    }
  });

  return {
    ...virtualizerState,
    enableScrollAnchor,
    components: {
      ...virtualizerState.components,
      container: 'div',
    },
    container: slot.always(props.container, {
      defaultProps: {
        ref: scrollViewRef,
      },
      elementType: 'div',
    }),
  };
}
